/**
 * Zod schemas (data props only), prop labels and preview samples of the
 * Flotte registrations that are not plain usages of a shared card. The four
 * shared-card usages reuse `../shared/schemas`.
 */

import { z } from 'zod';

import {
  CLAIMS_TABLE_PREVIEW_PROPS,
  SHARED_CARD_PROP_LABELS,
  entityInfoCardDataSchema,
  type ClaimsTableCardDataProps,
  type DocumentsCardDataProps,
  type EntityInfoCardDataProps,
  type GuaranteesTableCardDataProps,
} from '../shared/schemas';
import type { FleetListCardProps } from './FleetListCard';
import type { VehicleContractCardProps } from './VehicleContractCard';

// ---------------------------------------------------------------------------
// Data-prop types
// ---------------------------------------------------------------------------

export type FleetListCardDataProps = Pick<FleetListCardProps, 'vehicles' | 'loading' | 'error'>;
export type VehicleContractCardDataProps = Pick<
  VehicleContractCardProps,
  'values' | 'loading' | 'error' | 'contractPath'
>;

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

export const fleetVehicleSchema = z.object({
  id: z.string(),
  idNpol: z.string().nullable(),
  idVeh: z.string().nullable(),
  immatriculation: z.string().nullable(),
  marqueModele: z.string().nullable(),
  dateEntree: z.string().nullable(),
  dateSortie: z.string().nullable(),
  statut: z.string().nullable(),
});

export const fleetListCardDataSchema = z.object({
  vehicles: z.array(fleetVehicleSchema),
  loading: z.boolean().optional(),
  error: z.string().nullable().optional(),
}) satisfies z.ZodType<FleetListCardDataProps>;

export const vehicleContractCardDataSchema = entityInfoCardDataSchema.extend({
  contractPath: z.string().nullable().optional(),
}) satisfies z.ZodType<VehicleContractCardDataProps>;

// ---------------------------------------------------------------------------
// Prop labels (admin UI)
// ---------------------------------------------------------------------------

export const FLEET_PROP_LABELS = {
  ...SHARED_CARD_PROP_LABELS,
  vehicles: 'Véhicules',
  contractPath: 'Lien vers le contrat',
} as const;

// ---------------------------------------------------------------------------
// Preview samples (admin config page) — the fiches' sample rows
// ---------------------------------------------------------------------------

export const FLEET_LIST_PREVIEW_PROPS: FleetListCardDataProps = {
  vehicles: [
    {
      id: '8340-31601',
      idNpol: '8340',
      idVeh: '31601',
      immatriculation: 'DF-325-GT',
      marqueModele: 'FORD ROVER',
      dateEntree: '08/09/2026',
      dateSortie: null,
      statut: 'EN COURS',
    },
    {
      id: '8340-30963',
      idNpol: '8340',
      idVeh: '30963',
      immatriculation: 'AB-123-CD',
      marqueModele: 'RENAULT MASTER',
      dateEntree: '20/03/2022',
      dateSortie: '15/01/2026',
      statut: 'SORTI',
    },
  ],
};

export const VEHICLE_DETAILS_PREVIEW_PROPS: EntityInfoCardDataProps = {
  values: {
    immatriculation: 'DF-325-GT',
    marque: 'FORD',
    modele: 'ROVER',
    miseEnCirculation: '01/09/2026',
    conducteur: null,
    dateEntree: '08/09/2026',
    statut: 'EN COURS',
  },
};

export const VEHICLE_CONTRACT_PREVIEW_PROPS: VehicleContractCardDataProps = {
  values: {
    numero: '11096938S0001',
    produit: 'FLOTTE AUTOMOBILE (Autre)',
    assureur: 'GROUPAMA LOIRE BRETAGNE',
    assure: 'CCV BEAUMANOIR',
    dateEffet: '01/03/2023',
    dateEcheance: null,
    primeAnnuelle: '235 390,14 €',
    fractionnement: 'Semestriel',
  },
  contractPath: '/extranet/demo/contrats/8340',
};

export const VEHICLE_GUARANTEES_PREVIEW_PROPS: GuaranteesTableCardDataProps = {
  rows: [{ id: 'garantie-0', libelle: 'Responsabilité Civile', plafond: 0, franchise: 0 }],
};

export const VEHICLE_CLAIMS_PREVIEW_PROPS: ClaimsTableCardDataProps = CLAIMS_TABLE_PREVIEW_PROPS;

export const VEHICLE_DOCUMENTS_PREVIEW_PROPS: DocumentsCardDataProps = {
  documents: [
    { id: '210742', categorie: null, nom: 'Test document véhicule', date: '01/10/2026' },
  ],
};
